// Detalhe do White Label (§6.2) — header (logo/nome/slug/loja/badge)
// + Tabs (Dados & Tema | Configurações | Métodos | Relatórios).
// Wave 1: TODAS as tabs em modo leitura.
import { ServerIcon } from "lucide-react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs"
import { useUrlTab } from "@/hooks/use-url-tab"
import { useAdminWhiteLabel } from "@/services/admin"

import { getInitials } from "../utils"
import { AppCredentialsTab } from "./app-credentials-tab"
import { ConfigurationsTab } from "./configurations-tab"
import { InsuranceTypesTab } from "./insurance-types-tab"
import { MethodsTab } from "./methods-tab"
import { ReportsTab } from "./reports-tab"
import { ThemeTab } from "./theme-tab"
import { ActiveBadge } from "./white-label-card"

// A aba viaja na URL (?tab=): permite mandar link direto para "Tipos de Seguro"
// e sobrevive ao F5, que antes jogava o usuário de volta em "Dados & Tema".
const TAB_VALUES = [
  "theme",
  "configurations",
  "methods",
  "reports",
  "insurance-types",
  "app-credentials",
] as const
const DEFAULT_TAB = "theme"

const panelPrefix = "pages.adminWhiteLabels"

// Skeleton do detalhe: header + tabs + conteúdo.
const DetailSkeleton = () => (
  <div
    data-testid="white-label-detail-skeleton"
    className="space-y-4"
  >
    <Skeleton className="h-16 w-full rounded-xl" />
    <Skeleton className="h-10 w-96 max-w-full rounded-lg" />
    <Skeleton className="h-64 w-full rounded-xl" />
  </div>
)

type WhiteLabelDetailProps = {
  uuid: string
}

export const WhiteLabelDetail = ({
  uuid,
}: WhiteLabelDetailProps) => {
  const { t } = useTranslation()
  const { data, isLoading, isError, refetch } =
    useAdminWhiteLabel(uuid)
  const [activeTab, handleTabChange] = useUrlTab(
    TAB_VALUES,
    DEFAULT_TAB
  )

  if (isLoading) return <DetailSkeleton />

  if (isError || !data) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-6">
        <p className="text-center text-sm text-red-700">
          {t(`${panelPrefix}.detail.loadError`)}
        </p>
        <Button
          type="button"
          variant="outline"
          onClick={() => refetch()}
        >
          {t(`${panelPrefix}.retry`)}
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header de identificação do WL. */}
      <div className="flex flex-wrap items-center gap-4 rounded-xl border bg-card p-4 shadow-sm">
        <Avatar className="h-12 w-12 rounded-lg">
          {data.logoUrl && (
            <AvatarImage
              src={data.logoUrl}
              alt={data.name}
            />
          )}
          <AvatarFallback className="rounded-lg text-sm font-semibold">
            {getInitials(data.name)}
          </AvatarFallback>
        </Avatar>
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="text-base font-semibold">
            {data.name}
          </span>
          <span className="font-mono text-xs text-muted-foreground">
            {data.slug}
          </span>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-3">
          <span className="text-sm text-muted-foreground">
            {t(`${panelPrefix}.storeLabel`)}:{" "}
            {data.store?.name ?? "—"}
          </span>
          <ActiveBadge active={data.active} />
          {/* Atalho pro painel de Processadores JÁ filtrado neste WL —
              onde se configura o gateway por método (o badge de
              coerência da tab Métodos aponta pra cá). */}
          <Button asChild variant="outline" size="sm">
            <Link
              to={`/admin/processor-config?whiteLabel=${uuid}`}
            >
              <ServerIcon
                className="mr-1.5 size-4"
                aria-hidden="true"
              />
              {t(
                `${panelPrefix}.detail.processorConfigLink`
              )}
            </Link>
          </Button>
        </div>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={handleTabChange}
      >
        <TabsList>
          <TabsTrigger value="theme">
            {t(`${panelPrefix}.tabs.theme`)}
          </TabsTrigger>
          <TabsTrigger value="configurations">
            {t(`${panelPrefix}.tabs.configurations`)}
          </TabsTrigger>
          <TabsTrigger value="methods">
            {t(`${panelPrefix}.tabs.methods`)}
          </TabsTrigger>
          <TabsTrigger value="reports">
            {t(`${panelPrefix}.tabs.reports`)}
          </TabsTrigger>
          <TabsTrigger value="insurance-types">
            {t(`${panelPrefix}.tabs.insuranceTypes`)}
          </TabsTrigger>
          <TabsTrigger value="app-credentials">
            {t(`${panelPrefix}.tabs.appCredentials`)}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="theme" className="pt-2">
          <ThemeTab detail={data} />
        </TabsContent>
        <TabsContent
          value="configurations"
          className="pt-2"
        >
          <ConfigurationsTab uuid={uuid} />
        </TabsContent>
        <TabsContent value="methods" className="pt-2">
          <MethodsTab uuid={uuid} />
        </TabsContent>
        <TabsContent value="reports" className="pt-2">
          <ReportsTab uuid={uuid} />
        </TabsContent>
        <TabsContent
          value="app-credentials"
          className="pt-2"
        >
          <AppCredentialsTab uuid={uuid} />
        </TabsContent>
        <TabsContent
          value="insurance-types"
          className="pt-2"
        >
          <InsuranceTypesTab uuid={uuid} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
